import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DEFAULT_ADVANCED_SETTINGS } from '@/domain/templates/defaults';
import {
  toAdvancedSettings,
  toSchedulingRuleset,
} from '@/domain/templates/scheduling-rules.adapter';
import { withScalingMetric } from '@/domain/templates/staffing';
import { mapWizardStateToContract } from '@/domain/templates/template-mapper';
import { usePublishTemplate, useSaveTemplate, useUnpublishTemplate } from '@/hooks/use-templates';
import type { SavedTemplate } from '@/types/saved-template';
import type { AdvancedSettings, ShiftSet, TemplateBasics, WizardState } from '@/types/template';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const STORAGE_KEY = 'template-wizard-state';
const STORAGE_VERSION = '8.0'; // Increment when WizardState shape changes
const VERSION_KEY = 'template-wizard-version';

/**
 * BroadcastChannel name for multi-tab conflict detection (D36). Any tab
 * that writes the wizard state to localStorage broadcasts a `state-changed`
 * message; other tabs flip `hasExternalChanges = true` so the wizard can
 * surface a conflict warning before the user commits to a save.
 */
const BROADCAST_CHANNEL_NAME = 'template-wizard-state';

interface BroadcastMessage {
  type: 'state-changed';
  /** Stable per-tab identifier so a tab can ignore its own broadcasts. */
  origin: string;
}

/** Blank wizard state for the create-new flow. */
function getDefaultState(): WizardState {
  return {
    currentStep: 1,
    templateBasics: {
      name: '',
      type: 'standard',
      description: '',
      scope: {},
    },
    shifts: [],
    schedulingRules: toSchedulingRuleset(DEFAULT_ADVANCED_SETTINGS),
    lastSaved: null,
  };
}

/**
 * Restores the create-flow state from localStorage, falling back to the
 * default when it is missing, from another storage version, or malformed.
 */
function getInitialState(): WizardState {
  if (typeof window === 'undefined') return getDefaultState();
  try {
    const savedVersion = localStorage.getItem(VERSION_KEY);
    if (savedVersion !== STORAGE_VERSION) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem(VERSION_KEY, STORAGE_VERSION);
      return getDefaultState();
    }
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return getDefaultState();
    const parsed = JSON.parse(saved) as Partial<WizardState>;
    if (
      parsed &&
      typeof parsed === 'object' &&
      parsed.templateBasics &&
      Array.isArray(parsed.shifts) &&
      typeof parsed.currentStep === 'number' &&
      // Guard the contract-shaped ruleset too: a pre-contracts or partial state
      // without it would crash `toAdvancedSettings(state.schedulingRules)`.
      Array.isArray(parsed.schedulingRules?.rules)
    ) {
      return parsed as WizardState;
    }
    console.warn('Invalid saved state structure, using default');
    localStorage.removeItem(STORAGE_KEY);
    return getDefaultState();
  } catch (e) {
    console.error('Failed to parse saved wizard state:', e);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.setItem(VERSION_KEY, STORAGE_VERSION);
    return getDefaultState();
  }
}

/**
 * Wizard state with localStorage auto-save (2 s debounce) + multi-tab
 * conflict detection (D36). The hook owns the entire wizard's local
 * state; backend writes go through `saveToBackend` (create or update the
 * definition, never touching lifecycle) and `changeLifecycle` (publish or
 * unpublish the saved template, never saving), both routed through TanStack
 * Query mutations that handle cache invalidation.
 *
 * `hasExternalChanges` flips to `true` when another tab broadcasts a
 * state change; consumers should surface a "this template is being
 * edited in another tab" warning before allowing the user to save.
 */
export function useWizardState(initialTemplate?: SavedTemplate | null) {
  const [state, setState] = useState<WizardState>(() => {
    if (initialTemplate) {
      return {
        currentStep: 4, // Start on review step when editing
        templateBasics: {
          name: initialTemplate.name,
          type: initialTemplate.type,
          description: initialTemplate.description,
          scope: initialTemplate.scope,
          demandScaling: initialTemplate.demandScaling,
        },
        shifts: initialTemplate.shifts,
        schedulingRules: initialTemplate.schedulingRules,
        lastSaved: Date.now(),
      };
    }
    return getInitialState();
  });

  const [templateId, setTemplateId] = useState<string | null>(initialTemplate?.id ?? null);
  // The saved template's lifecycle, owned by the server and changed only through
  // the publish/unpublish actions; drives the header badge and lifecycle button.
  const [lifecycleStatus, setLifecycleStatus] = useState<LifecycleStatus>(
    initialTemplate?.status === 'published' ? 'published' : 'draft',
  );
  // Serialised definition as last saved (null until a new template is first
  // saved), so the workspace can block publishing changes that aren't saved.
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(() =>
    initialTemplate ? JSON.stringify(mapWizardStateToContract(state)) : null,
  );
  const hasUnsavedChanges = useMemo(
    () => savedSnapshot !== JSON.stringify(mapWizardStateToContract(state)),
    [savedSnapshot, state],
  );

  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [hasExternalChanges, setHasExternalChanges] = useState(false);

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The backend save currently in flight, if any; guards against re-entry.
  const inFlightSaveRef = useRef<Promise<SaveOutcome> | null>(null);
  // localStorage key, scoped by template identity so an edit (or draft) session
  // for one template can't bleed into the blank "create new" flow. Editing
  // template X persists under `…:X`; a fresh new template uses the bare key,
  // which `getInitialState` reads on mount. Without this scoping, the shared
  // key let a previously-edited template pre-fill a new template's wizard.
  const storageKeyRef = useRef<string>(
    initialTemplate?.id ? `${STORAGE_KEY}:${initialTemplate.id}` : STORAGE_KEY,
  );
  // Tab-stable random ID used to filter our own broadcasts.
  const tabIdRef = useRef<string>(crypto.randomUUID());
  const broadcastRef = useRef<BroadcastChannel | null>(null);

  // React Query mutations give proper cache invalidation
  const saveTemplateMutation = useSaveTemplate();
  const publishMutation = usePublishTemplate();
  const unpublishMutation = useUnpublishTemplate();

  // D36 — subscribe to multi-tab broadcasts.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
      return undefined;
    }
    const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    broadcastRef.current = channel;

    function handleMessage(event: MessageEvent<BroadcastMessage>) {
      if (event.data?.type === 'state-changed' && event.data.origin !== tabIdRef.current) {
        setHasExternalChanges(true);
      }
    }
    channel.addEventListener('message', handleMessage);

    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
      broadcastRef.current = null;
    };
  }, []);

  const performAutoSave = useCallback(async () => {
    setSaveStatus('saving');
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(storageKeyRef.current, JSON.stringify(state));
        localStorage.setItem(VERSION_KEY, STORAGE_VERSION);
        broadcastRef.current?.postMessage({
          type: 'state-changed',
          origin: tabIdRef.current,
        } satisfies BroadcastMessage);
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (e) {
      console.error('Failed to save wizard state:', e);
      setSaveStatus('error');
    }
  }, [state]);

  /**
   * Creates or updates the template on the backend. Never changes its
   * lifecycle: a published template stays published. On the first save of a
   * new template, re-keys the localStorage draft to the new id so it can't
   * pre-fill the next create flow.
   */
  const persistTemplate = useCallback(async (): Promise<SaveOutcome> => {
    setSaveStatus('saving');
    try {
      const result = await saveTemplateMutation.mutateAsync({
        state,
        existingId: templateId ?? undefined,
      });
      const created = templateId === null;
      if (created) {
        localStorage.removeItem(storageKeyRef.current);
        storageKeyRef.current = `${STORAGE_KEY}:${result.id}`;
      }
      // The server copy is now canonical and edit mode seeds from it, so drop
      // the local autosave. Unsaved edits are deliberately not recovered.
      localStorage.removeItem(storageKeyRef.current);
      // A pending debounced autosave would otherwise re-create the key we just
      // cleared.
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      setTemplateId(result.id);
      setSavedSnapshot(JSON.stringify(mapWizardStateToContract(state)));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
      return { kind: 'saved', id: result.id, created };
    } catch (error) {
      console.error('Failed to save template to backend:', error);
      setSaveStatus('error');
      return { kind: 'failed' };
    }
  }, [state, templateId, saveTemplateMutation]);

  /**
   * Saves the template, sharing a single in-flight request: a call made while a
   * save is still pending (e.g. a second Save click before a new template has an
   * id) resolves with that save's outcome rather than creating a duplicate. Any
   * pending debounced autosave is cancelled first, since the backend save makes
   * the local draft redundant.
   */
  const saveToBackend = useCallback((): Promise<SaveOutcome> => {
    if (inFlightSaveRef.current) return inFlightSaveRef.current;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    const pending = persistTemplate().finally(() => {
      inFlightSaveRef.current = null;
    });
    inFlightSaveRef.current = pending;
    return pending;
  }, [persistTemplate]);

  /**
   * Publishes or unpublishes the saved template via the lifecycle actions,
   * without saving. Returns false, leaving the status unchanged, when the
   * call fails or the template has never been saved.
   */
  const changeLifecycle = useCallback(
    async (target: LifecycleStatus): Promise<boolean> => {
      if (templateId === null) return false;
      setSaveStatus('saving');
      try {
        const mutation = target === 'published' ? publishMutation : unpublishMutation;
        await mutation.mutateAsync(templateId);
        setLifecycleStatus(target);
        setSaveStatus('idle');
        return true;
      } catch (error) {
        console.error('Failed to change template lifecycle:', error);
        setSaveStatus('error');
        return false;
      }
    },
    [templateId, publishMutation, unpublishMutation],
  );

  // Skip the autosave + broadcast that would otherwise fire on initial
  // mount — re-saving the just-loaded state would self-broadcast and
  // every freshly-opened tab would flag every other tab as "externally
  // changed" the moment they all subscribed.
  const isInitialMount = useRef(true);

  // Debounced auto-save: 2 s after the last `state` change.
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return undefined;
    }
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      void performAutoSave();
    }, 2000);
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [state, performAutoSave]);

  const retrySave = useCallback(() => {
    void performAutoSave();
  }, [performAutoSave]);

  /**
   * Clears the `hasExternalChanges` flag — consumers call this when the
   * user acknowledges the multi-tab warning and chooses to continue.
   */
  const acknowledgeExternalChanges = useCallback(() => {
    setHasExternalChanges(false);
  }, []);

  function updateTemplateBasics(basics: TemplateBasics) {
    setState((prev) => {
      // The demand metric is template-level; if it changed, force every scaling
      // shift onto the new metric so the preview and persisted config stay aligned.
      const metric = basics.demandScaling?.metric;
      const shifts =
        metric && metric !== prev.templateBasics.demandScaling?.metric
          ? prev.shifts.map((shift) => withScalingMetric(shift, metric))
          : prev.shifts;
      return { ...prev, templateBasics: basics, shifts, lastSaved: Date.now() };
    });
  }

  function addShift(shift: ShiftSet) {
    setState((prev) => ({ ...prev, shifts: [...prev.shifts, shift], lastSaved: Date.now() }));
  }

  function updateShift(shiftId: string, updatedShift: ShiftSet) {
    setState((prev) => ({
      ...prev,
      shifts: prev.shifts.map((s) => (s.id === shiftId ? updatedShift : s)),
      lastSaved: Date.now(),
    }));
  }

  function deleteShift(shiftId: string) {
    setState((prev) => ({
      ...prev,
      shifts: prev.shifts.filter((s) => s.id !== shiftId),
      lastSaved: Date.now(),
    }));
  }

  function setCurrentStep(step: 1 | 2 | 3 | 4) {
    setState((prev) => ({ ...prev, currentStep: step }));
  }

  function resetWizard() {
    setState(getDefaultState());
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(storageKeyRef.current);
      localStorage.setItem(VERSION_KEY, STORAGE_VERSION);
    } catch (e) {
      console.error('Failed to clear wizard state:', e);
    }
  }

  /**
   * Replace the scheduling rules from the flat Advanced Settings form view,
   * converting to the canonical `SchedulingRuleset` via the adapter.
   */
  function updateAdvancedSettings(settings: AdvancedSettings) {
    setState((prev) => ({
      ...prev,
      schedulingRules: toSchedulingRuleset(settings),
      lastSaved: Date.now(),
    }));
  }

  /**
   * Merge a partial `AdvancedSettings` over the current rules, leaving
   * unspecified fields untouched. Used to seed a new template's rules from the
   * site's composed policy defaults without clobbering fields it doesn't cover.
   */
  function mergeAdvancedSettings(settings: Partial<AdvancedSettings>) {
    setState((prev) => {
      const current = toAdvancedSettings(prev.schedulingRules);
      return {
        ...prev,
        schedulingRules: toSchedulingRuleset({ ...current, ...settings }),
        lastSaved: Date.now(),
      };
    });
  }

  return {
    state,
    /** Derived flat view of `state.schedulingRules` for the Advanced Settings form. */
    advancedSettings: toAdvancedSettings(state.schedulingRules),
    saveStatus,
    /**
     * True while a backend save, publish or unpublish is in flight. Unlike
     * `saveStatus`, the localStorage autosave never affects it.
     */
    isPersisting:
      saveTemplateMutation.isPending || publishMutation.isPending || unpublishMutation.isPending,
    hasExternalChanges,
    acknowledgeExternalChanges,
    updateTemplateBasics,
    addShift,
    updateShift,
    deleteShift,
    setCurrentStep,
    updateAdvancedSettings,
    mergeAdvancedSettings,
    resetWizard,
    retrySave,
    saveToBackend,
    changeLifecycle,
    templateId,
    lifecycleStatus,
    hasUnsavedChanges,
  };
}

/** A saved template's server-owned lifecycle, as edited in the Build workspace. */
export type LifecycleStatus = 'draft' | 'published';

/** Result of {@link useWizardState}'s `saveToBackend`. */
export type SaveOutcome = { kind: 'saved'; id: string; created: boolean } | { kind: 'failed' };
